import { apiClient } from '../../../shared/lib/apiClient';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export interface PaymentSummary {
  id: string;
  status: string;
  amount: number;
  currency: string;
  paymentIntentId: string | null;
  clientSecret: string | null;
  createdAt: string;
}

export interface PaymentDetail extends PaymentSummary {
  subscriptionId: string;
  provider: string;
  providerPaymentId: string | null;
  paymentMethodType: string | null;
  paymentMethodLast4: string | null;
  feeAmount: number;
  netAmount: number;
  failureCode: string | null;
  failureMessage: string | null;
  processedAt: string | null;
  metadata: Record<string, unknown> | null;
}

export interface RefundSummary {
  id: string;
  status: string;
  amount: number;
  currency: string;
  reason: string;
  providerRefundId: string | null;
  createdAt: string;
}

export interface CreatePaymentIntentRequest {
  subscriptionId: string;
  paymentMethodId?: string;
}

export interface RefundRequest {
  amount?: number;
  reason: string;
}

export async function createPaymentIntent(
  req: CreatePaymentIntentRequest,
  idempotencyKey: string,
): Promise<PaymentSummary> {
  const res = await apiClient.post<ApiResponse<PaymentSummary>>('/api/v1/payments/intents', req, {
    headers: { 'Idempotency-Key': idempotencyKey },
  });
  return res.data;
}

export async function listPayments(params?: {
  skip?: number;
  take?: number;
  status?: string;
}): Promise<PaymentDetail[]> {
  const query = new URLSearchParams();
  if (params?.skip !== undefined) query.set('skip', String(params.skip));
  if (params?.take !== undefined) query.set('take', String(params.take));
  if (params?.status) query.set('status', params.status);
  const qs = query.toString();
  const res = await apiClient.get<ApiResponse<PaymentDetail[]>>(
    `/api/v1/payments${qs ? `?${qs}` : ''}`,
  );
  return res.data;
}

export async function getPayment(id: string): Promise<PaymentDetail> {
  const res = await apiClient.get<ApiResponse<PaymentDetail>>(`/api/v1/payments/${id}`);
  return res.data;
}

export async function confirmPaymentIntent(paymentId: string): Promise<PaymentDetail> {
  const res = await apiClient.post<ApiResponse<PaymentDetail>>(
    `/api/v1/payments/${paymentId}/confirm`,
    {},
  );
  return res.data;
}

export async function refundPayment(
  id: string,
  req: RefundRequest,
  idempotencyKey: string,
): Promise<RefundSummary> {
  const res = await apiClient.post<ApiResponse<RefundSummary>>(
    `/api/v1/payments/${id}/refund`,
    req,
    { headers: { 'Idempotency-Key': idempotencyKey } },
  );
  return res.data;
}
